"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { MessageSquare as MessageSquareCheck, Compass, FileCheck2, Hammer, ClipboardCheck, ArrowRight } from 'lucide-react';
interface StepItem {
  number: string;
  title: string;
  phase: string;
  description: string;
  details: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
  image: {
    src: string;
    srcset: string;
    sizes: string;
    alt: string;
  };
}
const steps: StepItem[] = [{
  number: "01",
  phase: "Step One",
  title: "Initial Consultation & Discovery",
  description: "We visit your property to evaluate architectural layouts, listen to your functional goals, assess budget feasibility, and define project scope.",
  details: "On-site space evaluation, preliminary timeline mapping, and design objectives alignment.",
  icon: MessageSquareCheck,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/process_step_consultation.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/process_step_consultation.png-wvc-srcset",
    sizes: "(max-width: 768px) 100vw, 400px",
    alt: "Homeowners and lead contractor discussing floor plans in an open living space"
  }
}, {
  number: "02",
  phase: "Step Two",
  title: "Design & Architectural Planning",
  description: "Our design team develops comprehensive architectural schematics, 3D renderings, and precise finish palettes tailored to your everyday lifestyle.",
  details: "Material specifications, interior elevations, electrical layouts, and custom millwork drawings.",
  icon: Compass,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/process_step_design_planning.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/process_step_design_planning.webp-wvc-srcset",
    sizes: "(max-width: 768px) 100vw, 400px",
    alt: "Architectural blueprint and material sample board on a drafting table"
  }
}, {
  number: "03",
  phase: "Step Three",
  title: "Permitting & Material Procurement",
  description: "We navigate local municipal zoning approvals, secure all building permits, and pre-order architectural finishes to protect construction schedules.",
  details: "Building authority documentation, structural reviews, and lead-time logistics coordination.",
  icon: FileCheck2,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/process_step_permits_materials.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/process_step_permits_materials.png-wvc-srcset",
    sizes: "(max-width: 768px) 100vw, 400px",
    alt: "Approved architectural documents and premium hardwood flooring samples"
  }
}, {
  number: "04",
  phase: "Step Four",
  title: "Dedicated Construction & Build",
  description: "Our craft teams execute the build with daily site supervision, dust protection, structural carpentry, and regular photo milestone updates.",
  details: "Structural framing, mechanical rough-ins, artisan tilework, and bespoke cabinetry installation.",
  icon: Hammer,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/process_step_construction_build.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/process_step_construction_build.png-wvc-srcset",
    sizes: "(max-width: 768px) 100vw, 400px",
    alt: "Skilled carpenters installing custom kitchen cabinetry during a whole-home renovation"
  }
}, {
  number: "05",
  phase: "Step Five",
  title: "Final Walkthrough & Handover",
  description: "We conduct an exhaustive room-by-room inspection to resolve punch-list details, hand over warranties, and officially welcome you into your completed home.",
  details: "Comprehensive quality punch list, systems orientation, and written craftsmanship warranties.",
  icon: ClipboardCheck,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/process_step_final_walkthrough.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/process_step_final_walkthrough.png-wvc-srcset",
    sizes: "(max-width: 768px) 100vw, 400px",
    alt: "Completed bright open-concept living and dining renovation ready for handover"
  }
}];
export default function RemodelingProcess() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15
  });
  return <section id="remodelingprocess" data-nav="light" ref={ref} className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`max-w-3xl mx-auto text-center mb-16 lg:mb-20 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <span className="text-xs uppercase tracking-widest font-semibold text-primary mb-3 block">
            Proven Five-Phase Methodology
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-normal tracking-tight text-foreground leading-tight">
            Our Whole-Home Remodeling Process
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            From the earliest exploratory sketches to the final day of construction,
            each stage is managed with clear milestones, accountable timelines, and
            uncompromising craftsmanship.
          </p>
        </div>

        <div className="relative">
          <div aria-hidden="true" className="hidden lg:block absolute left-1/2 top-12 bottom-12 w-px bg-border -translate-x-1/2 pointer-events-none" />

          <div className="space-y-12 lg:space-y-16">
            {steps.map((step, index) => {
            const isEven = index % 2 === 1;
            const Icon = step.icon;
            return <div key={step.number} data-index={index} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 items-center transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
              transitionDelay: `${index * 120}ms`
            }}>
                  <div className={`lg:col-span-6 ${isEven ? "lg:order-2 lg:pl-10" : "lg:order-1 lg:pr-10"}`}>
                    <Card className="border border-border/80 bg-card shadow-sm hover:shadow-md transition-shadow duration-300">
                      <CardContent className="p-6 sm:p-8">
                        <div className="flex items-center justify-between mb-4">
                          <span className="text-xs font-mono tracking-wider font-semibold text-primary uppercase bg-primary/10 px-3 py-1 rounded-sm">
                            {step.phase}
                          </span>
                          <span className="text-3xl font-serif text-muted-foreground/40 font-semibold">
                            {step.number}
                          </span>
                        </div>

                        <div className="flex items-start gap-3.5 mb-3">
                          <div className="p-2.5 rounded-md bg-secondary text-secondary-foreground shrink-0 mt-0.5">
                            <Icon className="w-5 h-5 text-primary" />
                          </div>
                          <h3 className="text-xl sm:text-2xl font-serif font-medium text-card-foreground leading-snug">
                            {step.title}
                          </h3>
                        </div>

                        <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-4">
                          {step.description}
                        </p>

                        <div className="border-t border-border pt-4 mt-4">
                          <span className="text-xs font-semibold uppercase tracking-wider text-foreground/80 block mb-1">
                            Key Deliverables
                          </span>
                          <p className="text-xs sm:text-sm text-muted-foreground">
                            {step.details}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <div className={`lg:col-span-6 ${isEven ? "lg:order-1 lg:pr-10" : "lg:order-2 lg:pl-10"}`}>
                    <div className="overflow-hidden rounded-xl border border-border shadow-sm group">
                      <AspectRatio ratio={16 / 10}>
                        <img src={step.image.src} data-wvc-srcset={step.image.srcset} data-wvc-sizes={step.image.sizes} alt={step.image.alt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      </AspectRatio>
                    </div>
                  </div>
                </div>;
          })}
          </div>
        </div>

        <div className={`mt-16 lg:mt-24 pt-10 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-6 text-center sm:text-left transition-all duration-700 delay-500 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div>
            <h4 className="text-lg font-serif font-medium text-foreground">
              Ready to begin planning your renovation?
            </h4>
            <p className="text-sm text-muted-foreground mt-1">
              Schedule an in-home architectural consultation with our Brookhaven renovation specialists.
            </p>
          </div>

          <Button asChild size="lg" className="shrink-0 group">
            <Link to="/ContactRequestAnEstimatePage">
              <span>Request An Estimate</span>
              <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}